import { apiClient } from '@/lib/api/client'
import { env } from '@/config/env'
import type { Artwork, ArtworkStatus } from '@/types/entities'
import type { PaginationParams, PaginatedResponse } from '@/types/common'

export interface CreateArtworkData {
  name: string
  description?: string
  allowWholesale?: boolean
  matureContent?: boolean
}

export interface UpdateArtworkData {
  name?: string
  description?: string
  tags?: number[]
  allowWholesale?: boolean
  matureContent?: boolean
  status?: ArtworkStatus
}

// Below the hosting proxy's ~32 MB request limit, with room for the multipart envelope.
export const SINGLE_REQUEST_MAX = 24 * 1000 * 1000
// Each piece of a larger file; the API accepts up to 20 MB a piece and 32 pieces.
export const PIECE_SIZE = 16 * 1000 * 1000
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000

function newUploadId(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && 'randomUUID' in c) return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

async function uploadInPieces(
  artworkId: number,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  const uploadId = newUploadId()
  const count = Math.ceil(file.size / PIECE_SIZE)
  let sent = 0
  for (let i = 0; i < count; i++) {
    const piece = file.slice(i * PIECE_SIZE, Math.min(file.size, (i + 1) * PIECE_SIZE))
    await apiClient.put(`/api/artworks/${artworkId}/image/chunks/${i}`, piece, {
      headers: { 'Content-Type': 'application/octet-stream', 'x-upload-id': uploadId },
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: (e) => {
        // Joining and converting on the server is the last 5%.
        if (onProgress) onProgress(((sent + (e.loaded || 0)) / file.size) * 95)
      },
    })
    sent += piece.size
  }
  await apiClient.post(
    `/api/artworks/${artworkId}/image/assemble`,
    { uploadId, count },
    { timeout: UPLOAD_TIMEOUT_MS }
  )
  if (onProgress) onProgress(100)
}

export const artworkApi = {
  // List artworks
  getArtworks: async (
    status: ArtworkStatus,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<Artwork>> => {
    const response = await apiClient.get<Artwork[]>('/api/artworks', {
      params: {
        status,
        start: pagination.start,
        limit: pagination.limit,
      },
    })

    // The total comes in the `count` response header (handlers/artwork-handler.js `all`). It was
    // read from `response.data.count`, which a JSON array never has, so the total was always 0,
    // the pager never showed, and an artist with 163 artworks saw only the first 10. Fall back
    // to /count when the header is not readable (a cross-origin call without it exposed).
    const artworks = response.data || []
    const header = Number(response.headers?.['count'])
    const total = Number.isFinite(header) && response.headers?.['count'] != null
      ? header
      : await artworkApi.getArtworkCount(status)
    return {
      data: artworks,
      total,
      start: pagination.start,
      limit: pagination.limit,
    }
  },

  // Get artwork count
  getArtworkCount: async (status?: ArtworkStatus): Promise<number> => {
    const response = await apiClient.get('/api/artworks/count', {
      params: status ? { status } : {},
    })
    return response.data.count
  },

  // Get single artwork
  getArtwork: async (artworkId: number): Promise<Artwork> => {
    const response = await apiClient.get(`/api/artworks/${artworkId}`)
    return response.data
  },

  // Create artwork
  createArtwork: async (data: CreateArtworkData): Promise<Artwork> => {
    const response = await apiClient.post('/api/artworks', data)
    return response.data
  },

  // Update artwork
  updateArtwork: async (
    artworkId: number,
    data: UpdateArtworkData
  ): Promise<Artwork> => {
    const response = await apiClient.patch(`/api/artworks/${artworkId}`, data)
    return response.data
  },

  // Delete artwork
  deleteArtwork: async (artworkId: number): Promise<void> => {
    await apiClient.delete(`/api/artworks/${artworkId}`)
  },

  // Upload artwork image
  // A request over about 32 MB never reaches the API (the hosting proxy answers it with a
  // plain 500), so a larger file goes up in pieces that the API joins (lib/chunked-upload.js).
  // Uploads get far longer than the default 30 s timeout: a print-resolution file on a slow
  // connection takes minutes, and the server then converts it.
  uploadArtworkImage: async (
    artworkId: number,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    if (file.size > SINGLE_REQUEST_MAX) {
      return uploadInPieces(artworkId, file, onProgress)
    }
    const formData = new FormData()
    formData.append('artwork', file)

    await apiClient.post(`/api/artworks/${artworkId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100
          onProgress(progress)
        }
      },
    })
  },

  // Publish artwork
  publishArtwork: async (artworkId: number): Promise<void> => {
    await apiClient.post(`/api/artworks/${artworkId}/publish`)
  },

  // Unpublish artwork
  unpublishArtwork: async (artworkId: number): Promise<void> => {
    await apiClient.delete(`/api/artworks/${artworkId}/publish`)
  },

  // Replicate markups
  replicateMarkups: async (
    artworkId: number,
    frameTypeId: number
  ): Promise<void> => {
    await apiClient.post(`/api/artworks/${artworkId}/replicateMarkups`, {
      frameType: frameTypeId,
    })
  },

  // Get artwork preview image URL - pre-generated thumbnails (500x500, ~200KB)
  // All admin UI uses previews; full images are only used on backend for publishing
  getArtworkImageUrl: (artworkId: number): string => {
    return `${env.apiUrl}/artwork-ms/api/internal/artworks/${artworkId}/preview`
  },
}

